import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import PageFooter from '@/pages/home/components/PageFooter';
import premiumBG from "@/assets/premium_cover.jpg";
import premiumCTA from "@/assets/premium_cta.jpg";

const premiumBenefits = [
  {
    icon: 'ri-award-line',
    title: 'Certificate of Participation',
    desc: 'Receive an official GNA certificate recognizing your commitment to AI-powered business growth. Shareable on LinkedIn and professional profiles.',
    highlight: 'Official Recognition',
    color: 'from-amber-50 to-yellow-50',
    border: 'border-amber-200',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
  },
  {
    icon: 'ri-team-line',
    title: 'GNA AI Business Community',
    desc: 'Join an exclusive, curated network of AI-forward business owners, founders, and growth leaders. Access ongoing discussions, resources, and peer support.',
    highlight: 'Exclusive Network',
    color: 'from-green-50 to-green-100',
    border: 'border-green-200',
    iconBg: 'bg-green-100',
    iconColor: 'text-green-700',
  },
  {
    icon: 'ri-medal-line',
    title: 'Discounted AI Certification (i3Cert)',
    desc: 'Get exclusive discounts on internationally recognized AI certification exams through i3Cert Inc. Validate your AI knowledge with a globally respected credential.',
    highlight: 'Career Advancement',
    color: 'from-emerald-50 to-green-50',
    border: 'border-emerald-200',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-700',
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Post-Summit Implementation Session',
    desc: 'An exclusive follow-up session with expert guidance to help you implement what you learned. Turn summit insights into real business results with hands-on support.',
    highlight: 'Expert Guidance',
    color: 'from-yellow-50 to-amber-50',
    border: 'border-yellow-200',
    iconBg: 'bg-yellow-100',
    iconColor: 'text-yellow-700',
  },
  {
    icon: 'ri-file-copy-line',
    title: 'Premium Templates & Workflows',
    desc: 'Ready-to-use AI automation templates and workflow blueprints you can deploy immediately in your business. Includes ChatGPT prompts, Zapier workflows, and more.',
    highlight: 'Instant Value',
    color: 'from-green-50 to-emerald-50',
    border: 'border-green-200',
    iconBg: 'bg-green-100',
    iconColor: 'text-green-700',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Priority AI Consulting Access',
    desc: 'Get priority access to GNA\'s AI consulting team for personalized business transformation support. Skip the queue and get expert advice tailored to your business.',
    highlight: 'Priority Support',
    color: 'from-amber-50 to-yellow-50',
    border: 'border-amber-200',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
  },
];

const comparison = [
  { feature: 'Full 3-hour summit access', free: true, premium: true },
  { feature: 'Live AI demonstrations', free: true, premium: true },
  { feature: '4 expert-led sessions', free: true, premium: true },
  { feature: 'Live Q&A participation', free: true, premium: true },
  { feature: 'AI Starter Toolkit', free: true, premium: true },
  { feature: 'Prompt Library', free: true, premium: true },
  { feature: 'Replay access', free: true, premium: true },
  { feature: 'Certificate of Participation', free: false, premium: true },
  { feature: 'GNA AI Business Community', free: false, premium: true },
  { feature: 'Discounted i3Cert Exams', free: false, premium: true },
  { feature: 'Post-Summit Implementation Session', free: false, premium: true },
  { feature: 'Premium Templates & Workflows', free: false, premium: true },
  { feature: 'Priority AI Consulting Access', free: false, premium: true },
];

const PremiumPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Page Header */}
      <div className="relative bg-gradient-to-br from-green-800 to-green-950 pt-28 pb-20 px-4 md:px-8 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src={premiumBG}
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <span className="inline-block px-4 py-1.5 rounded-full bg-amber-400/20 border border-amber-400/30 text-amber-300 text-xs font-semibold uppercase tracking-widest mb-4">
            Premium Access
          </span>
          <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-4">
            Move from Learning to
            <span className="block bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">
              Real Transformation
            </span>
          </h1>
          <p className="text-green-200/80 text-base md:text-lg max-w-xl mx-auto mb-8">
            Premium access is designed for serious business owners who want to go beyond the summit and implement AI strategies with expert support.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/register"
              className="px-8 py-4 rounded-full bg-amber-400 text-green-900 font-bold text-base hover:bg-amber-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              Get Premium Access – $100
            </Link>
            <Link
              to="/register"
              className="px-8 py-4 rounded-full border-2 border-white/30 text-white font-semibold text-base hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
            >
              Register Free Instead
            </Link>
          </div>
          <p className="text-green-300/60 text-xs mt-4">One-time investment · Premium upgrade available after free registration</p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-16">

        {/* Benefits Grid */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3">
              What's Included in Premium
            </h2>
            <p className="text-gray-500 text-base max-w-xl mx-auto">
              Six exclusive benefits designed to accelerate your AI business transformation beyond the summit.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {premiumBenefits.map((b) => (
              <div
                key={b.title}
                className={`p-6 rounded-2xl bg-gradient-to-br ${b.color} border ${b.border} hover:-translate-y-1 transition-transform duration-200 cursor-default`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${b.iconBg}`}>
                    <i className={`${b.icon} text-xl ${b.iconColor}`}></i>
                  </div>
                  <span className="px-2.5 py-1 rounded-full bg-white/70 text-gray-600 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap">
                    {b.highlight}
                  </span>
                </div>
                <h4 className="font-bold text-gray-900 text-base mb-2">{b.title}</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Comparison Table */}
       

        {/* Final CTA */}
        <div className="relative rounded-3xl overflow-hidden">
          <img
            src={premiumCTA}
            alt="Premium CTA Background"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-green-800/92 to-green-700/88"></div>
          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8 px-8 md:px-12 py-12">
            <div className="text-center lg:text-left">
              <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-3">
                Ready to Transform Your Business?
              </h3>
              <p className="text-white/80 text-base max-w-lg">
                Join serious business owners who are investing in their AI future. Premium access is your fast-track to real implementation.
              </p>
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 mt-5">
                {['Certificate', 'Community', 'Templates', 'Consulting', 'Certification'].map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-white/20 text-white text-xs font-semibold whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div className="text-center shrink-0">
              <p className="text-white/70 text-sm mb-1">One-time investment</p>
              <p className="text-5xl font-extrabold text-white mb-4">$100</p>
              <Link
                to="/register"
                className="inline-block px-8 py-4 rounded-full bg-amber-400 text-green-900 font-bold text-base hover:bg-amber-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                Get Premium Access →
              </Link>
              <p className="text-white/60 text-xs mt-3">Available after free registration</p>
            </div>
          </div>
        </div>
      </main>

      <PageFooter />
    </div>
  );
};

export default PremiumPage;
